import React, { useEffect, useState, useCallback } from "react";
import { Select, Input } from "antd";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { getLogs } from "../../apis/log";
import { AuditLog } from "../../interfaces/Log";
import CommonPagination from "../../components/CommanPagination";

const { Search } = Input;

const EVENT_STYLES: Record<string, string> = {
  created: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  updated: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  deleted: "bg-red-50 text-red-600 ring-1 ring-red-200",
};

const MODEL_LABELS: Record<string, string> = {
  Patient:       "Patient",
  Visit:         "Visit",
  VisitEntry:    "IPD Entry",
  LabOrder:      "Lab Order",
  PharmacySale:  "Pharmacy Sale",
  PurchaseOrder: "Purchase Order",
};

const CATEGORY_FILTER_COLORS: Record<string, string> = {
  Clinical:       "bg-blue-100 text-blue-700",
  Administrative: "bg-amber-100 text-amber-700",
  System:         "bg-gray-100 text-gray-600",
};

const SENSITIVE_FIELDS = new Set(["phone", "email", "mobile"]);

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function humanizeKey(key: string): string {
  let prefix = "";
  let rest = key;

  if (key.startsWith("history_")) {
    prefix = "History: ";
    rest = key.slice(8);
  } else if (key.startsWith("layout_")) {
    rest = key.slice(7);
  }

  const humanized = rest
    .replace(/_id$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return prefix + humanized;
}

function maskPhone(value: string): string {
  const digits = String(value).replace(/\D/g, "");
  return digits.length >= 4 ? `••••••${digits.slice(-4)}` : "••••";
}

function maskEmail(value: string): string {
  const [local, domain] = String(value).split("@");
  if (!domain) return "••••";
  return `${local[0]}${"•".repeat(Math.max(1, local.length - 1))}@${domain}`;
}

function extractDraftText(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.blocks)) {
      return parsed.blocks.map((b: any) => b.text).filter(Boolean).join(" ") || "—";
    }
  } catch {}
  return raw;
}

function tryFormatDate(value: string): string | null {
  try {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return format(d, "dd MMM yyyy, h:mm a");
  } catch {}
  return null;
}

function displayValue(key: string, value: any): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (value[0]?.label !== undefined) return value.map((v: any) => v.label).join(", ");
    return value.map(String).join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  if (SENSITIVE_FIELDS.has(key.toLowerCase())) {
    return key.toLowerCase().includes("email") ? maskEmail(String(value)) : maskPhone(String(value));
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" && value.startsWith("{") && value.includes('"blocks"')) {
    return extractDraftText(value);
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}[T ]/.test(value)) {
    const formatted = tryFormatDate(value);
    if (formatted) return formatted;
  }
  return String(value);
}

interface DiffRowProps {
  field: string;
  oldVal?: any;
  newVal?: any;
  event: string;
}

const DiffRow: React.FC<DiffRowProps> = ({ field, oldVal, newVal, event }) => {
  const label = humanizeKey(field);

  if (event === "created") {
    return (
      <div className="flex items-start gap-3 py-1.5">
        <span className="w-44 shrink-0 text-xs text-gray-400">{label}</span>
        <span className="text-xs font-medium text-emerald-700">{displayValue(field, newVal)}</span>
      </div>
    );
  }

  if (event === "deleted") {
    return (
      <div className="flex items-start gap-3 py-1.5">
        <span className="w-44 shrink-0 text-xs text-gray-400">{label}</span>
        <span className="text-xs line-through text-red-500">{displayValue(field, oldVal)}</span>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="w-44 shrink-0 text-xs text-gray-400">{label}</span>
      <span className="text-xs line-through text-red-400">{displayValue(field, oldVal)}</span>
      <span className="text-xs text-gray-300 shrink-0">→</span>
      <span className="text-xs font-medium text-emerald-700">{displayValue(field, newVal)}</span>
    </div>
  );
};

interface LogRowProps {
  log: AuditLog;
  expanded: boolean;
  onToggle: () => void;
}

const LogRow: React.FC<LogRowProps> = ({ log, expanded, onToggle }) => {
  const initials = getInitials(log.subjectLabel || log.modelType);
  const bgColors = ["bg-indigo-500", "bg-teal-500", "bg-violet-500", "bg-rose-500", "bg-amber-500"];
  const bg = bgColors[(log.modelId ?? 0) % bgColors.length];

  const diffFields = log.changes
    ? Array.from(
        new Set([
          ...Object.keys(log.changes.old ?? {}),
          ...Object.keys(log.changes.new ?? {}),
        ])
      )
    : [];

  const hasChanges = diffFields.length > 0;

  return (
    <>
      <tr
        className={`border-b border-gray-100 transition-colors ${hasChanges ? "cursor-pointer hover:bg-gray-50" : ""}`}
        onClick={hasChanges ? onToggle : undefined}
      >
        {/* Patient / Subject */}
        <td className="py-3 pl-4 pr-3 sm:pl-6">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-9 h-9 rounded-full text-white text-sm font-semibold shrink-0 ${bg}`}>
              {initials}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">
                  {log.subjectLabel || `${log.modelType} #${log.modelId}`}
                </span>
                {log.subjectMrn && (
                  <span className="text-xs text-gray-400 font-mono">
                    MRN-{log.subjectMrn.replace(/\//g, "").padStart(4, "0")}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400">
                {log.createdAt ? format(new Date(log.createdAt), "dd MMM yyyy, h:mm a") : "—"}
              </span>
            </div>
          </div>
        </td>

        {/* Record type */}
        <td className="px-3 py-3 text-sm whitespace-nowrap">
          <span className="inline-flex items-center rounded px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600">
            {MODEL_LABELS[log.modelType] ?? log.modelType}
          </span>
        </td>

        {/* Event */}
        <td className="px-3 py-3 text-sm whitespace-nowrap">
          <span className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold capitalize ${EVENT_STYLES[log.event] ?? "bg-gray-100 text-gray-600"}`}>
            {log.event}
          </span>
        </td>

        {/* User */}
        <td className="px-3 py-3 text-sm">
          <div className="font-medium text-gray-900">{log.userName}</div>
          {log.userRole && <div className="text-xs text-gray-400">{log.userRole}</div>}
        </td>

        {/* Expand */}
        <td className="px-3 py-3 text-right pr-4 sm:pr-6">
          {hasChanges && (
            expanded
              ? <ChevronUpIcon className="w-4 h-4 text-gray-400 ml-auto" />
              : <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-auto" />
          )}
        </td>
      </tr>

      {/* Expanded diff */}
      {expanded && hasChanges && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-6 py-3 border-b border-gray-100">
            <div className="divide-y divide-gray-100">
              {diffFields.map((field) => (
                <DiffRow
                  key={field}
                  field={field}
                  oldVal={log.changes?.old?.[field]}
                  newVal={log.changes?.new?.[field]}
                  event={log.event}
                />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

interface LogsProps {
  visitId?: number;
}

const Logs: React.FC<LogsProps> = ({ visitId }) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [meta, setMeta] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const [filters, setFilters] = useState({
    category: "",
    event: "",
    subject_label: "",
  });

  const fetchLogs = useCallback((params: Record<string, any> = {}) => {
    setLoading(true);
    const baseParams: Record<string, any> = { per_page: 20 };
    if (visitId) baseParams.visit_id = visitId;
    getLogs({ ...baseParams, ...params })
      .then((res: any) => {
        setLogs(res.data ?? []);
        setMeta(res.meta);
      })
      .finally(() => setLoading(false));
  }, [visitId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    const merged = { ...filters, ...overrides };
    setFilters(merged);
    const params: Record<string, any> = { page: 1 };
    if (merged.category) params.category = merged.category;
    if (merged.event) params.event = merged.event;
    if (merged.subject_label) params.subject_label = merged.subject_label;
    fetchLogs(params);
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    const params: Record<string, any> = { page, per_page: pageSize || 20 };
    if (filters.category) params.category = filters.category;
    if (filters.event) params.event = filters.event;
    if (filters.subject_label) params.subject_label = filters.subject_label;
    fetchLogs(params);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {!visitId && (
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-base font-semibold leading-6 text-gray-900">Audit Log</h1>
            <p className="mt-1 text-sm text-gray-500">Track all changes made across the clinic.</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-4 flex flex-wrap gap-3">
        {!visitId && (
          <Search
            placeholder="Search patient..."
            allowClear
            className="w-52"
            onSearch={(val) => applyFilters({ subject_label: val })}
            onChange={(e) => {
              if (!e.target.value) applyFilters({ subject_label: "" });
            }}
          />
        )}

        <Select
          placeholder="Category"
          allowClear
          className="w-44"
          value={filters.category || undefined}
          onChange={(val) => applyFilters({ category: val ?? "" })}
          options={[
            { label: "Clinical", value: "Clinical" },
            { label: "Administrative", value: "Administrative" },
            { label: "System", value: "System" },
          ]}
        />

        <Select
          placeholder="Event"
          allowClear
          className="w-36"
          value={filters.event || undefined}
          onChange={(val) => applyFilters({ event: val ?? "" })}
          options={[
            { label: "Created", value: "created" },
            { label: "Updated", value: "updated" },
            { label: "Deleted", value: "deleted" },
          ]}
        />
      </div>

      {/* Table */}
      <div className="flow-root mt-6">
        <div className="overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide sm:pl-6">
                      Patient / Subject
                    </th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Record
                    </th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Action
                    </th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      By
                    </th>
                    <th className="px-3 py-3.5" />
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-sm text-gray-400">
                        Loading…
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-sm text-gray-400">
                        No audit logs found.
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <LogRow
                        key={log.id}
                        log={log}
                        expanded={expandedIds.has(log.id)}
                        onToggle={() => toggleExpand(log.id)}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Logs;
